<template>
  <v-container>
    <!-- Export dialog -->
    <ExportRangeDialog
      v-model="showRangeSelection"
      unit="day"
      :title="$filters.capitalize($t('production.export_hint'))"
      :subtitle="exportProducer ? $filters.capitalize(exportProducer.display_name) : ''"
      @update:model-value="onExportDialog"
      @export="download"
    ></ExportRangeDialog>

    <!-- Sticky header: title and controls, then the filters -->
    <div class="topbar">
      <div class="topbar__row">
        <div class="topbar__badge">
          <v-icon size="22">mdi-wind-turbine</v-icon>
        </div>
        <div class="topbar__title min-width-0">
          <div class="topbar__name">{{ $filters.capitalize($t("production.section_wind")) }}</div>
          <div class="topbar__sub">
            <span>{{ wind_productions.length }} {{ $t("machine.producers", wind_productions.length) }}</span>
            <span class="topbar__dot"></span>
            <span>{{ stations.length }} {{ $t("production.stations") }}</span>
            <template v-if="lastRefresh">
              <span class="topbar__dot"></span>
              <span>{{ $t("production.last_refresh") }} {{ $filters.formatTime(lastRefresh) }}</span>
            </template>
          </div>
        </div>

        <div class="topbar__actions">
          <v-text-field
            v-model="search"
            class="topbar__search"
            density="compact"
            variant="solo-filled"
            flat
            rounded="lg"
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            :placeholder="$filters.capitalize($t('generic.filter'))"
          ></v-text-field>

          <v-btn
            icon
            variant="tonal"
            color="primary"
            size="small"
            :loading="loading"
            :title="$filters.capitalize($t('production.refresh'))"
            @click="updateData"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>

      <div class="topbar__row topbar__row--filters">
        <span class="topbar__label">
          <v-icon size="x-small" class="mr-1">mdi-filter-variant</v-icon>{{ $filters.capitalize($t("generic.filter")) }}
        </span>
        <v-chip
          v-for="f in filters"
          :key="f.key"
          size="small"
          :variant="activeFilters.includes(f.key) ? 'flat' : 'tonal'"
          :color="activeFilters.includes(f.key) ? f.color : undefined"
          :prepend-icon="f.icon"
          class="filter-chip"
          @click="toggleFilter(f.key)"
        >
          {{ $filters.capitalize($t(f.label)) }}
          <span class="filter-chip__count">{{ f.count }}</span>
        </v-chip>
        <v-btn
          v-if="activeFilters.length || search"
          size="x-small"
          variant="text"
          prepend-icon="mdi-close"
          @click="clearFilters"
        >
          {{ $filters.capitalize($t("limitations.reset_filters")) }}
        </v-btn>
        <span class="topbar__result">
          <b>{{ filteredProductions.length }}</b> / {{ wind_productions.length }} {{ $t("machine.producers", wind_productions.length) }}
        </span>
      </div>
      <v-progress-linear v-if="loading" indeterminate color="primary" height="2" class="topbar__progress"></v-progress-linear>
    </div>

    <!-- KPI tiles -->
    <div class="kpi-grid">
      <v-card flat class="kpi kpi--blue">
        <div class="kpi__badge"><v-icon size="20">mdi-flash</v-icon></div>
        <div class="min-width-0">
          <div class="kpi__label">{{ $filters.capitalize($t("production.power")) }}</div>
          <div class="kpi__value">
            {{ $filters.formatFloat(totalPower) }}
            <span class="kpi__unit">{{ $t("unity.kw") }}</span>
          </div>
          <div class="kpi__hint">{{ reportingCount }} / {{ wind_productions.length }} {{ $t("production.reporting") }}</div>
        </div>
      </v-card>
      <v-card flat class="kpi kpi--purple">
        <div class="kpi__badge"><v-icon size="20">mdi-weather-windy</v-icon></div>
        <div class="min-width-0">
          <div class="kpi__label">{{ $filters.capitalize($t("production.wind_mean")) }}</div>
          <div class="kpi__value">
            {{ $filters.formatFloat(avgWind) }}
            <span class="kpi__unit">{{ $t("unity.ms") }}</span>
          </div>
          <div class="kpi__hint">{{ $t("production.wind_mean_hint") }}</div>
        </div>
      </v-card>
      <v-card flat class="kpi kpi--green">
        <div class="kpi__badge"><v-icon size="20">mdi-wind-turbine-check</v-icon></div>
        <div class="min-width-0">
          <div class="kpi__label">{{ $filters.capitalize($t("production.producers_running")) }}</div>
          <div class="kpi__value">
            {{ runningCount }}
            <span class="kpi__unit">/ {{ wind_productions.length }}</span>
          </div>
          <div class="kpi__hint">{{ limitedCount }} {{ $t("production.limited") }}</div>
        </div>
      </v-card>
      <v-card flat class="kpi kpi--amber">
        <div class="kpi__badge"><v-icon size="20">mdi-speedometer-slow</v-icon></div>
        <div class="min-width-0">
          <div class="kpi__label">{{ $filters.capitalize($t("production.last_underproduction")) }}</div>
          <div class="kpi__value">
            {{ underprodCount }}
            <span class="kpi__unit">{{ $t("production.current_underproduction") }}</span>
          </div>
          <div class="kpi__hint">{{ wind_productions.length - runningCount }} {{ $t("production.stopped") }}</div>
        </div>
      </v-card>
      <v-card flat class="kpi kpi--red">
        <div class="kpi__badge"><v-icon size="20">mdi-bell-alert</v-icon></div>
        <div class="min-width-0">
          <div class="kpi__label">{{ $filters.capitalize($t("alarms.label")) }}</div>
          <div class="kpi__value">
            {{ alarmCount }}
            <span class="kpi__unit">{{ $t("production.current_underproduction") }}</span>
          </div>
          <div class="kpi__hint">{{ alarmCompatibleCount }} {{ $t("machine.producers", alarmCompatibleCount) }} {{ $t("alarms.label") }}</div>
        </div>
      </v-card>
    </div>

    <!-- Producers, grouped by power station -->
    <v-card flat class="table-card">
      <!-- Phone: one card per producer, grouped by station -->
      <div class="m-list d-md-none">
        <template v-for="station in stations" :key="`m-${station.id}`">
          <div class="m-station" @click="station.id && openStation(station)">
            <v-icon size="small" color="primary">mdi-factory</v-icon>
            <span class="text-truncate">{{ $filters.capitalize(station.name) }}</span>
            <span class="m-station__stat">{{ $filters.formatFloat(station.power) }} {{ $t("unity.kw") }} &middot; {{ station.running }}/{{ station.items.length }}</span>
          </div>
          <v-card
            v-for="item in station.items"
            :key="`m-${item.producer_id}`"
            flat
            class="m-card"
            :class="{ 'm-card--nodata': !item.time }"
            @click="openProducer(item)"
          >
            <span class="m-card__bar" :class="freshnessClass(item.created_at) || 'fresh'"></span>
            <div class="m-card__body">
              <div class="m-card__head">
                <div class="min-width-0">
                  <div class="m-card__name">{{ $filters.capitalize(item.display_name) }}</div>
                  <div class="m-card__sub">{{ item.producer_type }}</div>
                </div>
                <span class="status-pill ml-auto" :class="item.running ? 'status-pill--on' : 'status-pill--off'">
                  <span class="status-pill__dot"></span>
                  {{ $filters.capitalize($t(item.running ? "production.running" : "production.stopped")) }}
                </span>
              </div>
              <div class="m-card__grid">
                <div>
                  <div class="m-stat__label">{{ $t("telemetry.power") }}</div>
                  <div class="m-stat__value" :class="{ 'text-warning': isUnderTheoretical(item) }">
                    {{ $filters.formatFloat(item.power) }} <span class="m-stat__unit">{{ $t("unity.kw") }}</span>
                  </div>
                </div>
                <div>
                  <div class="m-stat__label">{{ $t("production.wind") }}</div>
                  <div class="m-stat__value">
                    <template v-if="hasValue(item.wind)">{{ $filters.formatFloat(item.wind) }} <span class="m-stat__unit">{{ $t("unity.ms") }}</span></template>
                    <span v-else class="text-disabled">&ndash;</span>
                  </div>
                </div>
                <div>
                  <div class="m-stat__label">{{ $t("production.dispo") }}</div>
                  <div class="m-stat__value">{{ $filters.formatPercentage(item.dispo) }} <span class="m-stat__unit">%</span></div>
                </div>
              </div>
              <div class="m-card__foot">
                <span class="fresh-chip" :class="freshnessClass(item.created_at) || 'fresh'">
                  <template v-if="item.created_at">{{ relativeTime(item.created_at) }}</template>
                  <template v-else>{{ $t("production.no_telemetry") }}</template>
                </span>
                <span class="row-flags ml-auto" @click.stop>
                  <v-icon size="small" :class="item.limitation_ongoing ? 'flag--on' : 'flag--off'" :color="item.limitation_ongoing ? 'primary' : undefined">mdi-tortoise</v-icon>
                  <v-icon size="small" :class="hasAlarm(item) ? 'flag--on' : 'flag--off'" :color="hasAlarm(item) ? 'error' : undefined">{{ hasAlarm(item) ? "mdi-bell-alert" : "mdi-bell-outline" }}</v-icon>
                  <v-icon size="small" :class="item.current_underprod ? 'flag--on' : 'flag--off'" :color="item.current_underprod ? 'warning' : undefined">mdi-speedometer-slow</v-icon>
                </span>
              </div>
            </div>
            <v-icon size="small" class="m-card__chevron">mdi-chevron-right</v-icon>
          </v-card>
        </template>
        <div v-if="!stations.length && !loading" class="text-center text-medium-emphasis py-6">
          {{ $filters.capitalize($t("production.no_producer")) }}
        </div>
      </div>

      <v-table class="wind-table d-none d-md-block" density="comfortable">
        <thead>
          <tr>
            <th
              v-for="h in headers"
              :key="h.key"
              :class="[`text-${h.align}`, { sortable: h.sortable, active: sortKey === h.key }]"
              :style="h.width ? { width: h.width } : null"
              @click="h.sortable && setSort(h.key)"
            >
              {{ h.title }}
              <v-icon v-if="h.sortable" size="x-small" class="sort-icon">
                {{ sortKey === h.key && sortDesc ? "mdi-arrow-down" : "mdi-arrow-up" }}
              </v-icon>
            </th>
            <th class="row-chevron"></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="station in stations" :key="station.id">
            <!-- Station header -->
            <tr
              class="station-row"
              :class="{ 'station-row--link': station.id }"
              :title="station.id ? $filters.capitalize($t('machine.open_station')) : null"
              @click="station.id && openStation(station)"
            >
              <td :colspan="headers.length + 1">
                <div class="station-head">
                  <v-btn
                    :icon="isCollapsed(station.id) ? 'mdi-chevron-right' : 'mdi-chevron-down'"
                    size="x-small"
                    variant="text"
                    @click.stop="toggleStation(station.id)"
                  ></v-btn>
                  <v-icon size="small" class="mr-1" color="primary">mdi-factory</v-icon>
                  <span class="station-name">{{ $filters.capitalize(station.name) }}</span>
                  <v-chip size="x-small" variant="tonal" class="ml-2">{{ station.items.length }}</v-chip>
                  <span class="station-stat">
                    <b>{{ $filters.formatFloat(station.power) }}</b> {{ $t("unity.kw") }}
                    <span class="text-medium-emphasis">&middot; {{ $filters.formatFloat(station.wind) }} {{ $t("unity.ms") }}</span>
                  </span>
                  <span class="station-stat text-medium-emphasis">
                    {{ station.running }} / {{ station.items.length }} {{ $t("production.producers_running") }}
                  </span>
                  <v-icon v-if="station.limited" size="small" class="ml-2" :title="$filters.capitalize($t('production.limited'))">mdi-tortoise</v-icon>
                  <v-icon v-if="station.alarms" size="small" color="error" class="ml-1" :title="$filters.capitalize($t('production.alarm_ongoing'))">mdi-bell-alert</v-icon>
                  <span v-if="station.id" class="station-open">
                    {{ $filters.capitalize($t("machine.open_station")) }}
                    <v-icon size="small">mdi-arrow-right</v-icon>
                  </span>
                </div>
              </td>
            </tr>

            <!-- Producers of the station -->
            <template v-if="!isCollapsed(station.id)">
              <tr
                v-for="item in station.items"
                :key="item.producer_id"
                class="producer-row producer-row--link"
                :class="{ 'producer-row--nodata': !item.time }"
                :title="$filters.capitalize($t('machine.open_producer'))"
                @click="openProducer(item)"
              >
                <td>
                  <div class="producer-cell">
                    <span
                      class="producer-bar"
                      :class="freshnessClass(item.created_at) || 'fresh'"
                      :title="item.created_at ? $filters.formatDateTime(item.created_at) : $t('production.no_telemetry')"
                    ></span>
                    <div class="min-width-0">
                      <router-link
                        class="producer-name"
                        :to="{ name: 'intervention_reports', query: { producer_id: item.producer_id } }"
                        @click.stop
                      >
                        {{ $filters.capitalize(item.display_name) }}
                      </router-link>
                      <div class="text-caption text-medium-emphasis">{{ item.producer_type }}</div>
                    </div>
                  </div>
                </td>
                <td class="text-center">
                  <div class="status-cell">
                    <span class="status-pill" :class="item.running ? 'status-pill--on' : 'status-pill--off'">
                      <span class="status-pill__dot"></span>
                      {{ $filters.capitalize($t(item.running ? "production.running" : "production.stopped")) }}
                    </span>
                  </div>
                </td>
                <td class="text-center">
                  <div class="power-cell" :class="{ 'power-cell--warn': isUnderTheoretical(item), 'power-cell--off': !item.time }">
                    <div class="power-cell__value">
                      {{ $filters.formatFloat(item.power) }}
                      <span class="power-cell__unit">{{ $t("unity.kw") }}</span>
                      <span v-if="hasValue(item.theoretical_prod)" class="power-cell__pct" :title="$filters.capitalize($t('production.theoretical'))">
                        {{ $filters.formatFloat(item.theoretical_prod) }}
                      </span>
                    </div>
                    <div class="power-cell__bar" v-if="powerBar(item) !== null">
                      <div class="power-cell__fill" :style="{ width: `${Math.round(powerBar(item) * 100)}%` }"></div>
                    </div>
                  </div>
                </td>
                <td class="text-center">
                  <span v-if="hasValue(item.wind)">
                    {{ $filters.formatFloat(item.wind) }}
                    <span class="text-caption text-medium-emphasis">{{ $t("unity.ms") }}</span>
                  </span>
                  <span v-else class="text-disabled">&ndash;</span>
                </td>
                <td class="text-center">
                  <div class="dispo-cell">
                    <v-progress-circular
                      :rotate="90"
                      :size="40"
                      :width="4"
                      :model-value="pct(item.dispo)"
                      :color="progressColor(item.dispo)"
                      :title="$filters.capitalize($t('production.dispo'))"
                    >
                      <span class="dispo-cell__value">{{ $filters.formatPercentage(item.dispo) }}</span>
                    </v-progress-circular>
                    <v-progress-circular
                      :rotate="90"
                      :size="40"
                      :width="4"
                      :model-value="pct(item.dispo_temps)"
                      :color="progressColor(item.dispo_temps)"
                      :title="$filters.capitalize($t('production.dispo_temps'))"
                    >
                      <span class="dispo-cell__value">{{ $filters.formatPercentage(item.dispo_temps) }}</span>
                    </v-progress-circular>
                  </div>
                </td>
                <td class="text-center">
                  <span v-if="item.current_underprod" class="fresh-chip over_3h">
                    {{ $filters.capitalize($t("production.current_underproduction")) }}
                    <span class="text-caption">({{ item.last_underprod }})</span>
                  </span>
                  <span v-else-if="item.last_underprod" class="text-body-2">{{ $filters.capitalize(item.last_underprod) }}</span>
                  <span v-else class="text-disabled">&ndash;</span>
                </td>
                <td class="text-center">
                  <template v-if="item.created_at">
                    <span class="fresh-chip" :class="freshnessClass(item.created_at) || 'fresh'">
                      {{ relativeTime(item.created_at) }}
                    </span>
                    <div class="text-caption text-medium-emphasis mt-1">{{ $filters.formatDateTime(item.created_at) }}</div>
                  </template>
                  <span v-else class="fresh-chip over_5h">{{ $t("production.no_telemetry") }}</span>
                </td>
                <td class="text-center" @click.stop>
                  <div class="row-actions">
                    <span class="row-flags">
                      <v-icon
                        size="small"
                        :class="item.limitation_ongoing ? 'flag--on' : 'flag--off'"
                        :color="item.limitation_ongoing ? 'primary' : undefined"
                        :title="$filters.capitalize($t(item.limitation_ongoing ? 'production.limited' : 'production.no_limitation'))"
                      >mdi-tortoise</v-icon>
                      <v-btn
                        icon
                        size="x-small"
                        variant="text"
                        class="flag-btn"
                        :class="hasAlarm(item) ? 'flag--on' : 'flag--off'"
                        :color="hasAlarm(item) ? 'error' : undefined"
                        :title="$filters.capitalize($t(hasAlarm(item) ? 'production.alarm_ongoing' : 'production.see_alarms'))"
                        :to="{ name: 'alarms', query: { producer_id: item.producer_id } }"
                        target="_blank"
                      >
                        <v-icon size="small">{{ hasAlarm(item) ? "mdi-bell-alert" : "mdi-bell-outline" }}</v-icon>
                      </v-btn>
                      <v-icon
                        size="small"
                        :class="item.current_underprod ? 'flag--on' : 'flag--off'"
                        :color="item.current_underprod ? 'warning' : undefined"
                        :title="$filters.capitalize($t(item.current_underprod ? 'production.underproduction_ongoing' : 'production.no_underproduction'))"
                      >mdi-speedometer-slow</v-icon>
                    </span>
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      color="primary"
                      :loading="showSpinner(item.producer_id)"
                      :title="$filters.capitalize($t('production.export_hint'))"
                      @click.prevent="openRangeSelection(item.producer_id)"
                    >
                      <v-icon>mdi-download</v-icon>
                    </v-btn>
                  </div>
                </td>
                <td class="row-chevron"><v-icon size="small">mdi-chevron-right</v-icon></td>
              </tr>
            </template>
          </template>
          <tr v-if="!stations.length && !loading">
            <td :colspan="headers.length + 1" class="text-center text-medium-emphasis py-6">
              {{ $filters.capitalize($t("production.no_producer")) }}
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment";
import { isNone } from "@/functions.js";
import utils from "@/mixins/utils";
import ExportRangeDialog from "@/components/ExportRangeDialog.vue";

// Producers shown on the page (the backend also accepts an empty list for every wind producer)
const PRODUCER_IDS = [
  1, 2, 3, 4, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
  21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37,
  38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54,
  55, 56, 57, 63, 64, 65, 66, 67, 68, 69, 70, 71,
  72, 73, 74, 75, 76, 77, 78, 79, 80, 81, 82, 83, 88, 89, 90, 91, 92,
  93, 94, 95, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105, 113, 114,
  116, 117, 123, 125, 126, 127, 128, 131, 139, 140, 141, 191, 204, 130, 109,
  106, 108, 135, 136, 207, 208, 137, 231, 233, 234, 235, 129, 236, 237, 238, 239, 240, 241, 242, 244, 122, 132, 120
];

export default {
  components: { ExportRangeDialog },
  mixins: [utils],

  computed: {
    headers() {
      return [
        { title: this.$filters.capitalize(this.$t("production.name")), key: "name", align: "left", sortable: true, width: "240px" },
        { title: this.$filters.capitalize(this.$t("generic.status")), key: "running", align: "center", sortable: true, width: "170px" },
        { title: this.$filters.capitalize(this.$t("production.power")), key: "power", align: "center", sortable: true, width: "190px" },
        { title: this.$filters.capitalize(this.$t("production.wind")), key: "wind", align: "center", sortable: true },
        { title: `${this.$filters.capitalize(this.$t("production.dispo"))} / ${this.$t("production.dispo_temps")}`, key: "dispo", align: "center", sortable: true, width: "130px" },
        { title: this.$filters.capitalize(this.$t("production.last_underproduction")), key: "underprod", align: "center", sortable: true },
        { title: this.$filters.capitalize(this.$t("production.last_data")), key: "created_at", align: "center", sortable: true },
        { title: this.$filters.capitalize(this.$t("actions.label")), key: "actions", align: "center", sortable: false }
      ];
    },

    filters() {
      const all = this.wind_productions;
      return [
        { key: "limited", label: "production.limited", icon: "mdi-tortoise", color: "primary",
          test: (p) => p.limitation_ongoing, count: all.filter((p) => p.limitation_ongoing).length },
        { key: "stopped", label: "production.stopped", icon: "mdi-wind-turbine-alert", color: "error",
          test: (p) => !p.running, count: all.filter((p) => !p.running).length },
        { key: "alarm", label: "production.alarm_ongoing", icon: "mdi-bell-alert", color: "error",
          test: (p) => this.hasAlarm(p), count: all.filter((p) => this.hasAlarm(p)).length },
        { key: "underprod", label: "production.underproduction_ongoing", icon: "mdi-speedometer-slow", color: "warning",
          test: (p) => !!p.current_underprod, count: all.filter((p) => !!p.current_underprod).length },
        { key: "nodata", label: "production.no_data", icon: "mdi-access-point-off", color: "grey-darken-1",
          test: (p) => isNone(p.time), count: all.filter((p) => isNone(p.time)).length }
      ];
    },

    filteredProductions() {
      const needle = (this.search || "").trim().toLowerCase();
      let items = this.wind_productions;
      // Every active filter must match (limitation, stopped, alarm, underproduction)
      this.activeFilters.forEach((key) => {
        const f = this.filters.find((x) => x.key === key);
        if (f) {
          items = items.filter(f.test);
        }
      });
      if (!needle) {
        return items;
      }
      return items.filter(
        (p) =>
          String(p.display_name || "").toLowerCase().includes(needle) ||
          String(p.power_station_name || "").toLowerCase().includes(needle)
      );
    },

    // Producers grouped by power station (stations by name, producers by the chosen column)
    stations() {
      const groups = new Map();
      this.filteredProductions.forEach((p) => {
        const id = p.power_station_id || 0;
        if (!groups.has(id)) {
          groups.set(id, {
            id,
            name: p.power_station_name || this.$t("machine.station"),
            items: [],
            power: 0,
            windSum: 0,
            windCount: 0,
            running: 0,
            limited: false,
            alarms: false
          });
        }
        const g = groups.get(id);
        g.items.push(p);
        g.power += Number(p.power) || 0;
        if (this.hasValue(p.wind)) {
          g.windSum += Number(p.wind);
          g.windCount += 1;
        }
        if (p.running) g.running += 1;
        if (p.limitation_ongoing) g.limited = true;
        if (this.hasAlarm(p)) g.alarms = true;
      });
      const dir = this.sortDesc ? -1 : 1;
      const value = (p) => {
        switch (this.sortKey) {
          case "name":
            return String(p.display_name || "").toLowerCase();
          case "running":
            return p.running ? 1 : 0;
          case "underprod":
            return p.current_underprod ? 1 : 0;
          case "created_at":
            return isNone(p.created_at) ? null : moment(p.created_at).valueOf();
          default:
            return this.hasValue(p[this.sortKey]) ? Number(p[this.sortKey]) : null;
        }
      };
      const compare = (a, b) => {
        const va = value(a);
        const vb = value(b);
        if (va === null && vb === null) return 0;
        if (va === null) return 1;
        if (vb === null) return -1;
        if (va < vb) return -dir;
        if (va > vb) return dir;
        return 0;
      };
      return Array.from(groups.values())
        .map((g) => ({ ...g, wind: g.windCount ? g.windSum / g.windCount : 0, items: [...g.items].sort(compare) }))
        .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    },

    exportProducer() {
      if (isNone(this.producer_id)) {
        return null;
      }
      return this.wind_productions.find((p) => p.producer_id === this.producer_id) || null;
    },

    totalPower() {
      return this.wind_productions.reduce((acc, p) => acc + (Number(p.power) || 0), 0);
    },

    avgWind() {
      const winds = this.wind_productions.filter((p) => this.hasValue(p.wind) && p.time).map((p) => Number(p.wind));
      return winds.length ? winds.reduce((a, b) => a + b, 0) / winds.length : 0;
    },

    runningCount() {
      return this.wind_productions.filter((p) => p.running).length;
    },

    reportingCount() {
      return this.wind_productions.filter((p) => !isNone(p.time)).length;
    },

    underprodCount() {
      return this.wind_productions.filter((p) => p.current_underprod).length;
    },

    limitedCount() {
      return this.wind_productions.filter((p) => p.limitation_ongoing).length;
    },

    alarmCount() {
      return this.wind_productions.filter((p) => this.hasAlarm(p)).length;
    },

    alarmCompatibleCount() {
      return this.wind_productions.filter((p) => p.is_alarm_compatible).length;
    }
  },

  methods: {
    hasValue(value) {
      return !isNone(value) && value !== "";
    },

    hasAlarm(item) {
      return !!item.alarm_ongoing && Object.keys(item.alarm_ongoing).length !== 0;
    },

    pct(value) {
      return Math.min(100, Math.max(0, (Number(value) || 0) * 100));
    },

    progressColor(value) {
      const v = parseFloat(value);
      if (Number.isNaN(v)) return "grey";
      if (v < 0.7) return "red";
      if (v < 0.8) return "orange";
      if (v < 0.9) return "#FFD700";
      if (v < 0.95) return "light-green";
      if (v <= 1.0) return "green";
      return "blue";
    },

    // Power under the theoretical production of the minimum curve
    isUnderTheoretical(item) {
      if (!item.time || !this.hasValue(item.theoretical_prod) || Number(item.theoretical_prod) <= 0) {
        return false;
      }
      return Number(item.power) < Number(item.theoretical_prod);
    },

    // Bar: power against the best of the 90 % quantile curve and the theoretical value
    powerBar(item) {
      const ref = Math.max(Number(item.power_curve_upper_90) || 0, Number(item.theoretical_prod) || 0);
      if (ref <= 0) {
        return null;
      }
      return Math.min(1, Math.max(0, (Number(item.power) || 0) / ref));
    },

    clearFilters() {
      this.activeFilters = [];
      this.search = "";
    },

    toggleFilter(key) {
      if (this.activeFilters.includes(key)) {
        this.activeFilters = this.activeFilters.filter((k) => k !== key);
      } else {
        this.activeFilters = [...this.activeFilters, key];
      }
    },

    openStation(station) {
      this.$router.push({ name: "wind_power_station", query: { power_station_id: station.id } });
    },

    openProducer(item) {
      this.$router.push({ name: "wind_producer", query: { producer_id: item.producer_id } });
    },

    setSort(key) {
      if (this.sortKey === key) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortKey = key;
        this.sortDesc = false;
      }
    },

    isCollapsed(stationId) {
      return this.collapsed.includes(stationId);
    },

    toggleStation(stationId) {
      if (this.isCollapsed(stationId)) {
        this.collapsed = this.collapsed.filter((id) => id !== stationId);
      } else {
        this.collapsed = [...this.collapsed, stationId];
      }
    },

    relativeTime(value) {
      const minutes = Math.max(0, Math.round(moment().diff(moment(value), "minutes")));
      if (minutes < 60) {
        return this.$t("production.minutes_ago", { n: minutes });
      }
      const hours = Math.round(minutes / 60);
      if (hours < 48) {
        return this.$t("production.hours_ago", { n: hours });
      }
      return this.$t("production.days_ago", { n: Math.round(hours / 24) });
    },

    freshnessClass(last) {
      if (isNone(last)) {
        return "over_5h";
      }
      const last_ = moment(last);
      if (last_ < this.limit_5_hour) {
        return "over_5h";
      } else if (last_ < this.limit_3_hour) {
        return "over_3h";
      } else if (last_ < this.limit_1_hour) {
        return "over_1h";
      }
      return "";
    },

    updateData() {
      this.loading = true;
      this.limit_1_hour = moment().subtract(1, "hours");
      this.limit_3_hour = moment().subtract(3, "hours");
      this.limit_5_hour = moment().subtract(5, "hours");
      this.$store
        .dispatch("telemetries/windCurrentFujin", { ids: PRODUCER_IDS })
        .then((items) => {
          this.wind_productions = Array.isArray(items) ? items : [];
        })
        .catch((error) => {
          console.error("wind current:", error);
        })
        .finally(() => {
          this.loading = false;
          this.lastRefresh = moment();
        });
    },

    openRangeSelection(prod_id) {
      this.producer_id = prod_id;
      this.showRangeSelection = true;
    },

    closeRangeSelection() {
      this.showRangeSelection = false;
      this.producer_id = undefined;
    },

    onExportDialog(open) {
      if (!open) {
        this.closeRangeSelection();
      }
    },

    download(range) {
      if (!range || isNone(this.producer_id)) {
        return;
      }
      this.$store.dispatch("onLoading");
      this.getFile("telemetries/wind/producer/export_csv/", "", "text/csv", "blob", {
        start: range.start,
        end: range.end,
        producer_id: this.producer_id
      });
      this.closeRangeSelection();
    },

    showSpinner(prod_id) {
      return this.showRangeSelection && this.producer_id === prod_id;
    }
  },

  mounted() {
    this.updateData();
    this.intervalId = window.setInterval(() => {
      this.updateData();
    }, 10 * 60 * 1000);
  },

  unmounted() {
    window.clearInterval(this.intervalId);
  },

  data() {
    return {
      intervalId: undefined,
      loading: false,
      lastRefresh: null,
      search: "",
      activeFilters: [],
      sortKey: "name",
      sortDesc: false,
      collapsed: [],
      wind_productions: [],
      limit_1_hour: moment().subtract(1, "hours"),
      limit_3_hour: moment().subtract(3, "hours"),
      limit_5_hour: moment().subtract(5, "hours"),
      showRangeSelection: false,
      producer_id: undefined
    };
  }
};
</script>

<style scoped>
/* Freshness of the last telemetry: bar and chip share one scale */
.over_1h {
  color: #c9a800;
}
.over_3h {
  color: #e07b00;
}
.over_5h {
  color: #d32f2f;
}
.fresh {
  color: #2e7d32;
}
.fresh-chip {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 500;
  white-space: nowrap;
  background: color-mix(in srgb, currentColor 12%, white);
}

/* Sticky header, pinned under the app bar while the table scrolls */
.topbar {
  position: sticky;
  top: var(--v-layout-top, 0px);
  z-index: 5;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.topbar::before {
  /* the solar band's gradient, reduced to a thin accent line */
  content: "";
  display: block;
  height: 3px;
  background: linear-gradient(90deg, #0093bf 0%, #1f9fd1 55%, #6b5b4f 100%);
}
.topbar__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
}
.topbar__row--filters {
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 8px;
  padding-bottom: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  background: #fafbfc;
}
.topbar__badge {
  width: 40px;
  height: 40px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #dff3f9;
  color: #0093bf;
}
.topbar__title {
  flex: 1 1 200px;
}
.topbar__name {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}
.topbar__sub {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  overflow: hidden;
}
.topbar__dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.3);
  flex-shrink: 0;
}
.topbar__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}
.topbar__search {
  width: 260px;
}
.topbar__search :deep(.v-field) {
  background: #f1f4f6;
}
.topbar__label {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.55);
  margin-right: 4px;
}
.filter-chip {
  cursor: pointer;
}
.filter-chip__count {
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 0.7rem;
  background: rgba(0, 0, 0, 0.08);
}
.topbar__result {
  margin-left: auto;
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
}
.topbar__progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
}

/* KPI tiles */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
}
.kpi--purple .kpi__badge {
  background: #ece6f7;
  color: #6a3fb5;
}
.kpi {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  height: 100%;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}
.kpi:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}
.kpi__badge {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kpi--blue .kpi__badge {
  background: #dff3f9;
  color: #0093bf;
}
.kpi--green .kpi__badge {
  background: #e3f4e4;
  color: #2e7d32;
}
.kpi--amber .kpi__badge {
  background: #fff3d6;
  color: #e08a00;
}
.kpi--red .kpi__badge {
  background: #fde7e7;
  color: #d32f2f;
}
.kpi__label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kpi__value {
  margin-top: 2px;
  font-size: 1.65rem;
  font-weight: 700;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.87);
}
.kpi__unit {
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
}
.kpi__hint {
  margin-top: 2px;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Table card */
.table-card {
  margin-top: 10px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}
.wind-table :deep(th) {
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.05em;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.55) !important;
  background: #fff;
  user-select: none;
}
.wind-table :deep(th.sortable) {
  cursor: pointer;
}
.wind-table :deep(th.sortable:hover),
.wind-table :deep(th.active) {
  color: rgb(var(--v-theme-primary)) !important;
}
.wind-table :deep(th .sort-icon) {
  opacity: 0;
  transition: opacity 0.15s ease;
}
.wind-table :deep(th.sortable:hover .sort-icon) {
  opacity: 0.5;
}
.wind-table :deep(th.active .sort-icon) {
  opacity: 1;
}
.wind-table :deep(td) {
  font-variant-numeric: tabular-nums;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04) !important;
}
.wind-table :deep(tbody tr.producer-row:hover td) {
  background-color: #f2f9fc;
}
/* Producers without telemetry stand out as a red-tinted line */
.wind-table :deep(tr.producer-row--nodata td) {
  background-color: #fdecec;
}
.wind-table :deep(tr.producer-row--nodata .producer-bar) {
  visibility: hidden; /* the red line already says it */
}
.wind-table :deep(tr.producer-row--nodata:hover td) {
  background-color: #fadcdc;
}
.wind-table :deep(tr.producer-row--link),
.wind-table :deep(tr.station-row--link) {
  cursor: pointer;
}
.wind-table :deep(tbody tr.producer-row--link:hover td) {
  background-color: #e8f4f9;
}
.station-open {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.4);
  white-space: nowrap;
  transition: color 0.15s ease;
}
tr:hover .station-open {
  color: rgb(var(--v-theme-primary));
}
/* Clickable rows: a chevron at the end that lights up with the row */
.row-chevron {
  width: 36px;
  padding: 0 8px 0 0 !important;
  text-align: right;
}
.row-chevron .v-icon {
  color: rgba(0, 0, 0, 0.26);
  transition: color 0.15s ease, transform 0.15s ease;
}
tr:hover .row-chevron .v-icon {
  color: rgb(var(--v-theme-primary));
  transform: translateX(2px);
}

.wind-table :deep(tr.station-row--link:hover td) {
  background: #d2e0e7;
}

/* Station header rows */
.wind-table :deep(tr.station-row td) {
  background: #dfe9ee;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
  padding: 4px 8px !important;
}
.station-head {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.station-name {
  font-weight: 700;
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.station-stat {
  margin-left: 18px;
  font-size: 0.85rem;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.wind-table :deep(tr.producer-row td:first-child) {
  padding-left: 28px;
}

/* Producer cell */
.producer-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.producer-bar {
  display: inline-block;
  width: 4px;
  height: 30px;
  border-radius: 2px;
  flex-shrink: 0;
  background-color: currentColor;
}
.producer-name {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
}
.producer-name:hover {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
}
.min-width-0 {
  min-width: 0;
}

/* Status cell */
.status-cell {
  display: inline-flex;
  align-items: center;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  background: color-mix(in srgb, currentColor 12%, white);
}
.status-pill--on {
  color: #2e7d32;
}
.status-pill--off {
  color: #d32f2f;
}
.status-pill__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}
.row-flags {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-right: 10px;
  padding-right: 10px;
  border-right: 1px solid rgba(0, 0, 0, 0.1);
}
.m-card .row-flags {
  margin-right: 0;
  padding-right: 0;
  border-right: 0;
}
.flag--off {
  color: rgba(0, 0, 0, 0.22);
}
.flag-btn {
  width: 22px;
  height: 22px;
}
.flag--on {
  filter: drop-shadow(0 0 1px rgba(0, 0, 0, 0.15));
}

/* Power cell */
.power-cell {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 140px;
  padding: 4px 0;
  color: #0093bf;
}
.power-cell--warn {
  color: #e08a00;
}
.power-cell--off {
  color: #9e9e9e;
}
.power-cell__value {
  display: inline-flex;
  align-items: baseline;
  font-size: 1.15rem;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
  font-variant-numeric: tabular-nums;
}
.power-cell--off .power-cell__value {
  color: rgba(0, 0, 0, 0.4);
}
.power-cell__unit {
  margin-left: 7px;
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
}
.power-cell__bar {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: color-mix(in srgb, currentColor 14%, white);
  overflow: hidden;
}
.power-cell__fill {
  height: 100%;
  border-radius: 3px;
  background: currentColor;
  transition: width 0.3s ease;
}
.power-cell__pct {
  margin-left: 8px;
  padding-left: 8px;
  border-left: 1px solid rgba(0, 0, 0, 0.12);
  font-size: 0.78rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
  font-variant-numeric: tabular-nums;
}

/* Availability gauges */
.dispo-cell {
  display: inline-flex;
  gap: 6px;
}
.dispo-cell__value {
  font-size: 0.65rem;
  font-weight: 600;
}

.row-actions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  white-space: nowrap;
}

/* Phone layout: cards instead of the table */
.m-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
}
.m-station {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  padding: 6px 4px 2px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.6);
}
.m-station__stat {
  margin-left: auto;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  white-space: nowrap;
}
.m-card {
  display: flex;
  align-items: stretch;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  cursor: pointer;
}
.m-card--nodata {
  background: #fdecec;
}
.m-card__bar {
  width: 4px;
  border-radius: 2px;
  background: currentColor;
  flex-shrink: 0;
}
.m-card__body {
  flex: 1;
  min-width: 0;
}
.m-card__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.m-card__name {
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.m-card__sub {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
}
.m-card__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px 10px;
}
.m-stat__label {
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.5);
  white-space: nowrap;
}
.m-stat__value {
  font-size: 0.95rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.m-stat__unit {
  font-size: 0.7rem;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.5);
}
.m-card__foot {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}
.m-card__chevron {
  align-self: center;
  color: rgba(0, 0, 0, 0.26);
}

@media (max-width: 1100px) {
  .kpi-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (max-width: 600px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
  /* Tiles readable on a phone: smaller badge, wrapping labels, lighter values */
  .kpi {
    padding: 10px 12px;
    gap: 10px;
  }
  .kpi__label {
    white-space: normal;
    line-height: 1.2;
  }
  .kpi__hint {
    white-space: normal;
    line-height: 1.2;
  }
  .topbar__row {
    flex-wrap: wrap;
  }
  .topbar__actions {
    width: 100%;
    margin-left: 0;
  }
  .topbar__search {
    flex: 1;
    width: auto;
  }
  .topbar__result {
    width: 100%;
    margin-left: 0;
  }
  .table-card__head {
    flex-wrap: wrap;
  }
  .table-card__search {
    width: 100%;
    max-width: none;
  }
}
</style>
